import type { FrameSpec, PlayerProfile, RankTier } from '@shared/types';
import { SERVER_URL, COMMON_HEADERS } from '@/config/serverUrl';

// Empty BASE = same-origin fetch (web → nginx proxy). Native scheme detected
// inside SERVER_URL falls back to the production host. `localhost:8787` was a
// dev convenience that broke on phones (localhost = the phone itself).
const BASE = SERVER_URL;

export type CosmeticSlot =
  | 'hat'
  | 'body'
  | 'cape'
  | 'victoryShout'
  | 'avatarFrame'
  | 'endgameFx'
  | 'matchBackground';

export interface AuthUser
  extends Omit<PlayerProfile, 'name' | 'mmrSolo' | 'mmrDuo' | 'streakSolo' | 'streakDuo'> {
  // Server-only fields; older clients (cached envelope) may lack them. Treated
  // as optional and back-filled from the legacy `mmr`/`winStreak` on adopt.
  mmrSolo?: number;
  mmrDuo?: number;
  streakSolo?: number;
  streakDuo?: number;
  email: string | null;
  username: string | null;
  displayName: string;
  avatarUrl: string | null;
  isGuest: boolean;
  role: 'USER' | 'MODERATOR' | 'ADMIN';
  personalBestMs: number | null;
  /** Player-authored victory shout (shown on win). Null = none set. */
  victoryShout: string | null;
  /** Times the player has changed each field. First change per field is free;
   * each subsequent change costs gems. Drives the cost label in the UI. */
  nameChangeCount: number;
  shoutChangeCount: number;
  equipped: Record<CosmeticSlot, string | null>;
  /** Resolved avatar frame render spec from /me (image overlay or border+glow). */
  frame?: FrameSpec | null;
}

export interface AuthEnvelope {
  token: string;
  user: AuthUser;
}

export interface ProviderAvailability {
  email: boolean;
  guest: boolean;
  google: boolean;
  apple: boolean;
  facebook: boolean;
  zalo: boolean;
  tiktok: boolean;
}

export class AuthApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

async function jsonReq<T>(path: string, init?: RequestInit, token?: string): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...COMMON_HEADERS,
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });
  if (!res.ok) {
    let code = `HTTP_${res.status}`;
    let msg = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      if (body.error) code = body.error;
      if (body.message) msg = body.message;
    } catch {
      /* non-JSON body */
    }
    throw new AuthApiError(code, msg, res.status);
  }
  return res.json() as Promise<T>;
}

export const authApi = {
  register(input: { email: string; username: string; password: string; displayName?: string }) {
    return jsonReq<AuthEnvelope>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  login(input: { identifier: string; password: string }) {
    return jsonReq<AuthEnvelope>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  guest() {
    return jsonReq<AuthEnvelope>('/auth/guest', { method: 'POST' });
  },
  me(token: string) {
    return jsonReq<{ user: AuthUser }>('/auth/me', {}, token);
  },
  providers() {
    return jsonReq<ProviderAvailability>('/auth/providers');
  },
  startOAuthUrl(provider: 'google' | 'apple' | 'facebook' | 'zalo' | 'tiktok'): string {
    return `${BASE}/auth/${provider}`;
  },

  /** Sign in with Apple (native): exchange Apple's identityToken for a session. */
  appleNative(input: { identityToken: string; fullName?: string; email?: string }) {
    return jsonReq<AuthEnvelope>('/auth/apple/native', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /** Permanently delete the signed-in account and all associated data. */
  deleteAccount(token: string) {
    return jsonReq<{ ok: true }>('/auth/me', { method: 'DELETE' }, token);
  },

  /** Upload a personal photo as the player's avatar. Returns the public URL. */
  async uploadAvatar(token: string, file: File): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/me/avatar/`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, ...COMMON_HEADERS },
      body: fd,
      credentials: 'include',
    });
    if (!res.ok) {
      let code = `HTTP_${res.status}`;
      let msg = res.statusText;
      try {
        const j = (await res.json()) as { error?: string; message?: string };
        if (j.error) code = j.error;
        if (j.message) msg = j.message;
      } catch {
        /* */
      }
      throw new AuthApiError(code, msg, res.status);
    }
    return (await res.json()) as { url: string };
  },

  async deleteAvatar(token: string): Promise<void> {
    await fetch(`${BASE}/me/avatar/`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}`, ...COMMON_HEADERS },
      credentials: 'include',
    });
  },

  /** Rename the player. Server validates 1-24 chars and trims whitespace. */
  updateDisplayName(token: string, displayName: string) {
    return jsonReq<{ user: AuthUser }>(
      '/auth/me',
      { method: 'PATCH', body: JSON.stringify({ displayName }) },
      token,
    );
  },

  /** Set the player's custom victory shout. Empty string clears it. */
  updateVictoryShout(token: string, victoryShout: string) {
    return jsonReq<{ user: AuthUser }>(
      '/auth/me',
      { method: 'PATCH', body: JSON.stringify({ victoryShout }) },
      token,
    );
  },
};

// Re-export so callers don't need to know about the shared package layout.
export type { RankTier };
